<template>
  <div
    ref="container"
    class="membership-card-stage"
    role="img"
    :aria-label="`Dacr Membership card for ${name}`"
  >
    <div class="membership-card" data-membership-card>
      <div class="membership-card-inner">
        <div v-if="shadow" class="membership-card-shadow" />

        <div class="membership-card-surface">
          <svg
            class="membership-card-glow"
            preserveAspectRatio="none"
            viewBox="0 0 410 239"
            aria-hidden="true"
          >
            <defs>
              <radialGradient
                :id="gradientId"
                data-membership-card-glow
                cx="390.819"
                cy="-45.927"
                r="398.762"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0" stop-color="#8de4f5" stop-opacity="0.5" />
                <stop offset="1" stop-color="#1a1d21" stop-opacity="0.2" />
              </radialGradient>
            </defs>
            <rect width="410" height="239" :fill="`url(#${gradientId})`" />
          </svg>

          <div class="membership-card-logo" aria-hidden="true">
            <DacrWordLogo
              class="membership-card-logo-layer membership-card-logo-highlight"
              data-membership-card-logo-highlight
            />
            <DacrWordLogo
              class="membership-card-logo-layer membership-card-logo-shadow"
              data-membership-card-logo-shadow
            />
            <DacrWordLogo
              class="membership-card-logo-layer membership-card-logo-base"
            />
          </div>

          <div class="membership-card-member">
            <div class="membership-card-member-label">Member</div>
            <div class="membership-card-member-name">{{ name }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useId } from 'vue'

import DacrWordLogo from '@/assets/svg/other/DacrWordLogo.svg'

const props = withDefaults(
  defineProps<{
    name: string
    interactive?: boolean
    shadow?: boolean
    tracking?: 'card' | 'page'
  }>(),
  {
    interactive: false,
    shadow: false,
    tracking: 'card',
  }
)

const container = ref<HTMLElement>()
const gradientId = `membership-card-glow-${useId().replace(/:/g, '')}`

let cleanup: (() => void) | undefined

onMounted(() => {
  if (!props.interactive || !container.value) return

  const card = container.value.querySelector<HTMLElement>(
    '[data-membership-card]'
  )
  const glow = container.value.querySelector<SVGRadialGradientElement>(
    '[data-membership-card-glow]'
  )
  const logoHighlight = container.value.querySelector<SVGElement>(
    '[data-membership-card-logo-highlight]'
  )
  const logoShadow = container.value.querySelector<SVGElement>(
    '[data-membership-card-logo-shadow]'
  )

  if (!card) return

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches

  if (prefersReducedMotion) return

  const pageTracking = props.tracking === 'page'

  if (
    pageTracking &&
    !window.matchMedia('(hover: hover) and (pointer: fine)').matches
  )
    return

  const maxTilt = 10
  const damping = 0.12
  const gxBase = 390.819
  const gyBase = -45.927
  const gxRange = 73.8
  const gyRange = 33.46
  const hlBaseX = 0
  const hlBaseY = 0.5
  const shBaseX = 0
  const shBaseY = -1
  const hlRangeX = 0.35
  const hlRangeY = 0.25

  let targetRX = 0
  let targetRY = 0
  let currentRX = 0
  let currentRY = 0
  let targetGX = gxBase
  let targetGY = gyBase
  let currentGX = gxBase
  let currentGY = gyBase
  let targetHLX = hlBaseX
  let targetHLY = hlBaseY
  let currentHLX = hlBaseX
  let currentHLY = hlBaseY
  let targetSHX = shBaseX
  let targetSHY = shBaseY
  let currentSHX = shBaseX
  let currentSHY = shBaseY
  let animationFrame = 0
  let hovering = false

  const loop = () => {
    currentRX += (targetRX - currentRX) * damping
    currentRY += (targetRY - currentRY) * damping
    currentGX += (targetGX - currentGX) * damping
    currentGY += (targetGY - currentGY) * damping
    currentHLX += (targetHLX - currentHLX) * damping
    currentHLY += (targetHLY - currentHLY) * damping
    currentSHX += (targetSHX - currentSHX) * damping
    currentSHY += (targetSHY - currentSHY) * damping

    card.style.setProperty('--membership-card-rx', `${currentRX.toFixed(3)}deg`)
    card.style.setProperty('--membership-card-ry', `${currentRY.toFixed(3)}deg`)
    glow?.setAttribute('cx', currentGX.toFixed(3))
    glow?.setAttribute('cy', currentGY.toFixed(3))

    if (logoHighlight) {
      logoHighlight.style.transform = `translate(${currentHLX.toFixed(3)}px, ${currentHLY.toFixed(3)}px) rotate(0.03deg)`
    }

    if (logoShadow) {
      logoShadow.style.transform = `translate(${currentSHX.toFixed(3)}px, ${currentSHY.toFixed(3)}px) rotate(0.03deg)`
    }

    const settled =
      Math.abs(targetRX - currentRX) < 0.01 &&
      Math.abs(targetRY - currentRY) < 0.01 &&
      Math.abs(targetGX - currentGX) < 0.01 &&
      Math.abs(targetGY - currentGY) < 0.01 &&
      Math.abs(targetHLX - currentHLX) < 0.005 &&
      Math.abs(targetHLY - currentHLY) < 0.005 &&
      Math.abs(targetSHX - currentSHX) < 0.005 &&
      Math.abs(targetSHY - currentSHY) < 0.005

    if (settled && !hovering) {
      animationFrame = 0
      return
    }

    animationFrame = requestAnimationFrame(loop)
  }

  const ensureLoop = () => {
    if (!animationFrame) animationFrame = requestAnimationFrame(loop)
  }

  const handlePointerMove = (event: PointerEvent) => {
    if (pageTracking && event.pointerType !== 'mouse') return

    const rect = card.getBoundingClientRect()
    let x: number
    let y: number

    if (pageTracking) {
      const dx = event.clientX - rect.left - rect.width / 2
      const dy = event.clientY - rect.top - rect.height / 2
      const distance = Math.hypot(dx, dy)

      if (!distance) return

      x = (dx / distance) * 0.5
      y = (dy / distance) * 0.5
      card.classList.add('is-tracking')
    } else {
      x = (event.clientX - rect.left) / rect.width - 0.5
      y = (event.clientY - rect.top) / rect.height - 0.5
    }

    targetRY = x * 2 * maxTilt
    targetRX = -y * 2 * maxTilt
    targetGX = gxBase - x * 2 * gxRange
    targetGY = gyBase - y * 2 * gyRange
    targetHLX = hlBaseX - x * 2 * hlRangeX
    targetHLY = hlBaseY - y * 2 * hlRangeY
    targetSHX = shBaseX + x * 2 * hlRangeX
    targetSHY = shBaseY + y * 2 * hlRangeY
    ensureLoop()
  }

  const handlePointerEnter = () => {
    hovering = true
    card.classList.add('is-tracking')
  }

  const handlePointerLeave = () => {
    hovering = false
    card.classList.remove('is-tracking')
    targetRX = 0
    targetRY = 0
    targetGX = gxBase
    targetGY = gyBase
    targetHLX = hlBaseX
    targetHLY = hlBaseY
    targetSHX = shBaseX
    targetSHY = shBaseY
    ensureLoop()
  }

  if (pageTracking) {
    window.addEventListener('pointermove', handlePointerMove, { passive: true })
    document.documentElement.addEventListener(
      'pointerleave',
      handlePointerLeave
    )
  } else {
    card.addEventListener('pointermove', handlePointerMove)
    card.addEventListener('pointerenter', handlePointerEnter)
    card.addEventListener('pointerleave', handlePointerLeave)
  }

  cleanup = () => {
    if (animationFrame) cancelAnimationFrame(animationFrame)

    if (pageTracking) {
      window.removeEventListener('pointermove', handlePointerMove)
      document.documentElement.removeEventListener(
        'pointerleave',
        handlePointerLeave
      )
    } else {
      card.removeEventListener('pointermove', handlePointerMove)
      card.removeEventListener('pointerenter', handlePointerEnter)
      card.removeEventListener('pointerleave', handlePointerLeave)
    }
  }
})

onBeforeUnmount(() => cleanup?.())
</script>

<style scoped>
.membership-card-stage {
  container-type: inline-size;
  position: relative;
  width: 100%;
  aspect-ratio: 410 / 245;
  transform-origin: top left;
}

.membership-card {
  --membership-card-rx: 0deg;
  --membership-card-ry: 0deg;

  position: absolute;
  top: 1.1659cqw;
  left: 0;
  width: 100cqw;
  height: 58.3341cqw;
  border-radius: 4cqw;
  transform: perspective(390.2439cqw) rotateX(var(--membership-card-rx))
    rotateY(var(--membership-card-ry));
  transition: transform 600ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}

.membership-card.is-tracking {
  transition: transform 80ms linear;
}

.membership-card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: inherit;
}

.membership-card-shadow {
  position: absolute;
  top: 4.8341cqw;
  right: 9.666cqw;
  bottom: 2.8341cqw;
  left: 11cqw;
  background: transparent;
  box-shadow:
    0 8.0488cqw 16.5854cqw rgb(0 0 0 / 50%),
    0 3.4146cqw 8.2927cqw rgb(0 0 0 / 25%),
    0 1.2195cqw 2.6829cqw rgb(0 0 0 / 25%);
}

.membership-card-surface {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  border-radius: inherit;
  background-color: #1a1d21;
  padding: 6.5854cqw;
  color: #fff;
  transform: translateZ(0);
}

.membership-card-glow {
  pointer-events: none;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: inherit;
}

.membership-card-logo {
  position: relative;
  z-index: 1;
  width: 28.7805cqw;
  height: 8.0488cqw;
  transform: translateZ(4.3902cqw);
}

.membership-card-logo-layer {
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  width: 28.7805cqw;
  height: auto;
  transform-origin: top left;
  will-change: transform;
}

.membership-card-logo-layer :deep(path) {
  fill: currentColor;
}

.membership-card-logo-highlight {
  color: rgb(255 255 255 / 75%);
  transform: translate(0, 0.5px) rotate(0.03deg);
}

.membership-card-logo-shadow {
  color: rgb(0 0 0 / 75%);
  transform: translate(0, -1px) rotate(0.03deg);
}

.membership-card-logo-base {
  color: #3e5c64;
}

.membership-card-member {
  position: relative;
  z-index: 1;
  min-width: 0;
  transform: translateZ(5.3659cqw);
}

.membership-card-member-label,
.membership-card-member-name {
  overflow: hidden;
  font-size: 4.878cqw;
  line-height: 6.8293cqw;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.membership-card-member-label {
  color: #888c90;
  font-weight: 400;
}

.membership-card-member-name {
  color: #fff;
  font-weight: 500;
}

@media (prefers-reduced-motion: reduce) {
  .membership-card {
    transition: none;
  }
}
</style>
